/* ============================================================
   Dathent — Library (new, pack-aligned)
   Replaces old Content.jsx layout. Card component is pack's LibraryCard.
   Data source: useContentFeedContext (existing filters/data hooks preserved).
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContentFeedContext } from '../state/ContentFeedContext.jsx';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import LibraryCard, { formatMetric } from '../components/library/LibraryCard';

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

function deriveDisplayDate(item) {
  const status = item?.status || item?.contentItem?.status;
  const ps = item?.publishSummary;
  const when = ps?.publishedAt || ps?.scheduledAt || item?.publishedAt || item?.scheduledAt || item?.contentItem?.updatedAt;
  if (!when) return 'Draft';
  const d = new Date(when);
  if (Number.isNaN(d.getTime())) return String(when);
  const prefix = status === 'published' ? '' : status === 'draft' ? 'Draft · ' : '';
  const date = `${d.toLocaleString('en', { month: 'short' })} ${String(d.getDate()).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${prefix}${date}`;
}

function derivePlatform(item) {
  const targets = item?.publishSummary?.targets;
  if (Array.isArray(targets) && targets.length > 0) {
    return PLATFORM_FROM_NAME(targets[0].platform);
  }
  return PLATFORM_FROM_NAME(item?.platform || item?.platforms?.[0] || item?.contentItem?.platform) || item?.platform || 'linkedin';
}

/**
 * Expand a content row into per-platform display cards.
 *
 * Source of truth is the union of two sets:
 *   1. The originating batch platforms (`row.platforms`) — i.e. what the user
 *      originally generated this content for.
 *   2. The actual publish_targets — the rows the user has scheduled / published.
 *
 * Earlier this function fell into two mutually-exclusive branches: if any
 * publish_target existed, *only* targets became cards. That meant scheduling
 * the Telegram variant of a multi-platform draft made the Instagram sibling
 * card vanish from Library, even though the content_item still has IG in its
 * batch.platforms list. Now we keep the IG card as a virtual draft so users
 * can still open it and schedule independently.
 *
 * Returns: [{ key, contentRow, targetId, platform, status, scheduledAt, publishedAt, metrics }]
 */
function expandContentToCards(row) {
  // `row` is the mapped row from ContentFeedContext.mapContentRow — it has
  // an already-resolved `platform` and `platforms[]` derived from publishSummary
  // → batchPlatforms → format fallback. Prefer it over the raw contentItem.
  const item = row?.contentItem || row;
  const targets = row?.publishSummary?.targets || [];
  const rowPlatform = row?.platform || row?.platforms?.[0];
  const rowPlatforms = row?.platforms?.length ? row.platforms : (rowPlatform ? [rowPlatform] : []);

  // Bucket publish_targets by normalized platform name. Two Telegram channels
  // produce one bucket with two entries; we want both to show as separate
  // cards so the user can see "TG · Channel A" and "TG · Channel B" rows.
  const targetsByPlatform = new Map();
  for (const t of targets) {
    const p = PLATFORM_FROM_NAME(t.platform) || t.platform;
    if (!p) continue;
    if (!targetsByPlatform.has(p)) targetsByPlatform.set(p, []);
    targetsByPlatform.get(p).push(t);
  }

  // Union: every platform the batch was generated for + every platform that
  // already has a publish_target (handles the case where a target exists for
  // a platform that wasn't in the original batch — rare but possible).
  const platformOrder = [];
  const seen = new Set();
  for (const p of rowPlatforms) {
    const norm = PLATFORM_FROM_NAME(p) || p;
    if (norm && !seen.has(norm)) { seen.add(norm); platformOrder.push(norm); }
  }
  for (const p of targetsByPlatform.keys()) {
    if (!seen.has(p)) { seen.add(p); platformOrder.push(p); }
  }

  // Empty fallback — no batch platforms, no targets. Render one card with
  // whatever platform we can guess from the content_item.
  if (platformOrder.length === 0) {
    return [{
      key: String(item.id),
      contentRow: row,
      targetId: null,
      platform: PLATFORM_FROM_NAME(rowPlatform || item?.platform) || rowPlatform || 'linkedin',
      status: item.status || 'draft',
      scheduledAt: null,
      publishedAt: null,
      metrics: null,
    }];
  }

  // Per-platform status for a virtual card (no publish_target yet for this
  // platform). We never reuse item.status === 'scheduled' here — that flag
  // flips as soon as ANY platform is scheduled, but for the others the right
  // user-facing state is still "Draft, click to schedule". Only true
  // item-level lifecycle states (generating / failed / archived) override.
  const virtualStatus = (() => {
    const s = item?.status;
    if (s === 'generating' || s === 'failed' || s === 'archived') return s;
    return 'draft';
  })();

  const cards = [];
  for (const p of platformOrder) {
    const bucket = targetsByPlatform.get(p) || [];
    if (bucket.length === 0) {
      // No publish_target yet for this platform → virtual draft card.
      cards.push({
        key: `${item.id}::${p}`,
        contentRow: row,
        targetId: null,
        platform: p,
        status: virtualStatus,
        scheduledAt: null,
        publishedAt: null,
        metrics: null,
      });
      continue;
    }
    // One card per target (two TG accounts → two cards).
    for (const t of bucket) {
      cards.push({
        key: `${item.id}::${t.id || p}`,
        contentRow: row,
        targetId: t.id,
        platform: p,
        status: t.status === 'published' ? 'published'
          : t.status === 'failed' ? 'failed'
          : t.status === 'queued' || t.status === 'pending' || t.status === 'claimed' ? 'scheduled'
          : t.status === 'needs_reconnect' || t.status === 'needs_manual_action' ? 'attention'
          : (item.status || t.status),
        scheduledAt: t.scheduledAt,
        publishedAt: t.publishedAt,
        metrics: t.metrics || null,
      });
    }
  }
  return cards;
}

// Per-card date: prefer the target's published/scheduled time so each platform
// card reflects its own timing instead of the content_item's overall timestamps.
function deriveCardDate(card) {
  const when = card.publishedAt || card.scheduledAt;
  if (when) {
    const d = new Date(when);
    if (!Number.isNaN(d.getTime())) {
      const prefix = card.status === 'published' ? '' : card.status === 'draft' ? 'Draft · ' : '';
      const date = `${d.toLocaleString('en', { month: 'short' })} ${String(d.getDate()).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      return `${prefix}${date}`;
    }
  }
  return deriveDisplayDate(card.contentRow);
}

// Only show a media kind badge for meaningful types (carousel/video/thread).
// The default "Image" badge is suppressed — it's visual noise when most posts
// don't carry a custom image and the cover falls back to the platform gradient.
function deriveMedia(item) {
  const format = item?.contentItem?.format || item?.format;
  if (!format) return null;
  const f = String(format).toLowerCase();
  if (f.includes('carousel') || f.includes('slide')) return 'carousel';
  if (f.includes('video') || f.includes('reel') || f.includes('short')) return 'video';
  if (f.includes('thread')) return 'thread';
  return null;
}

// Strip the "Generating[ optional N/N]: " prefix that leaks through when the
// item's title/body is just the raw generation prompt (e.g. failed/in-flight items).
function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

// Truncate to N chars at a word boundary + ellipsis (keeps card titles tidy).
function truncate(s, max) {
  if (!s || s.length <= max) return s || '';
  const sliced = s.slice(0, max);
  const lastSpace = sliced.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? sliced.slice(0, lastSpace) : sliced).replace(/[.,;:!?\s]+$/, '') + '…';
}

// Resolve the platform-specific copy when activeVersion.platformVersions has
// it. Backend stores per-platform variants in content_versions.platform_versions_json,
// then compacts to 240-char snippets for the list endpoint. Falls back to the
// shared body/caption/hook when no per-platform variant exists.
function platformBody(version, platform) {
  if (!version || !platform) return null;
  const pv = version.platformVersions || {};
  const aliasKey = platform === 'x' ? 'twitter' : platform === 'twitter' ? 'x' : null;
  const direct = pv[platform];
  if (typeof direct === 'string' && direct.trim()) return direct;
  if (direct && typeof direct === 'object' && typeof direct.body === 'string' && direct.body.trim()) return direct.body;
  if (aliasKey) {
    const aliased = pv[aliasKey];
    if (typeof aliased === 'string' && aliased.trim()) return aliased;
    if (aliased && typeof aliased === 'object' && typeof aliased.body === 'string' && aliased.body.trim()) return aliased.body;
  }
  return null;
}

// Prefer the AI-generated copy (activeVersion.hook|body|caption) over the raw
// generation prompt stored on contentItem (which is often the user's input).
// When the version has a per-platform variant (Threads/X/Telegram length caps,
// platform tone), use that one for THIS card so each platform card on a
// multi-target post shows its own copy.
function deriveTitle(item, platform = null) {
  const v = item?.activeVersion;
  const platformText = platformBody(v, platform);
  const hookOrBody = platformText || v?.hook || v?.body || v?.caption;
  const raw = hookOrBody
    || item?.contentItem?.title
    || item?.title
    || item?.contentItem?.body
    || item?.body
    || '';
  const cleaned = cleanPromptPrefix(raw).split('\n')[0];
  return truncate(cleaned, 60) || 'Untitled draft';
}

function deriveSnippet(item, platform = null) {
  const v = item?.activeVersion;
  const platformText = platformBody(v, platform);
  const body = platformText || v?.body || v?.caption || v?.hook;
  if (body) return cleanPromptPrefix(body);
  return cleanPromptPrefix(item?.contentItem?.body || item?.body || '');
}

function deriveCoverImage(item) {
  return item?.mediaPreview?.url
    || item?.activeVersion?.imageUrl
    || item?.activeVersion?.thumbnailUrl
    || item?.mediaUrls?.[0]
    || null;
}

// Compute engagement % client-side from raw counts when backend doesn't supply.
// Returns 0 (not null) when post has views but no interactions — so the engagement
// pill consistently appears for any post that has actually been seen.
function computeEngagement(m) {
  if (!m) return null;
  if (typeof m.engagement === 'number') return m.engagement;
  const views = Number(m.views || 0);
  if (views <= 0) return null;
  const interactions = Number(m.likes || 0) + Number(m.comments || 0) + Number(m.shares || 0) + Number(m.saves || 0);
  return (interactions / views) * 100;
}

function deriveMetrics(item) {
  const m = item?.metrics;
  if (!m) return null;
  return {
    views: m.views ?? 0,
    likes: m.likes ?? 0,
    comments: m.comments ?? 0,
    shares: m.shares ?? 0,
    engagement: computeEngagement(m),
  };
}

const TABS = [
  { id: 'all', label: 'All', status: 'all' },
  { id: 'published', label: 'Published', status: 'published' },
  { id: 'scheduled', label: 'Scheduled', status: 'scheduled' },
  { id: 'draft', label: 'Drafts', status: 'draft' },
  { id: 'attention', label: 'Needs attention', status: 'failed' },
];

const PLATFORMS = ['all', 'instagram', 'facebook', 'linkedin', 'x', 'tiktok', 'threads', 'telegram'];

export const Library = () => {
  const navigate = useNavigate();
  const { content, counts, loading, hasMore, loadMore, refreshContent } = useContentFeedContext();

  const [tab, setTab] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [query, setQuery] = useState('');
  const [view, setView] = useState('grid');
  const sentinelRef = useRef(null);

  // Infinite scroll: when sentinel comes into view, fetch next page.
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && hasMore && !loading) {
            loadMore?.();
          }
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore, content?.length]);

  // Re-fetch when tab changes (server-side status filter)
  useEffect(() => {
    const tabSpec = TABS.find((t) => t.id === tab) || TABS[0];
    refreshContent({ status: tabSpec.status, background: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const tabsWithCounts = useMemo(() => {
    const c = counts || {};
    return TABS.map((t) => ({
      ...t,
      count: t.status === 'all' ? (c.all || 0) : (c[t.status] || 0),
    }));
  }, [counts]);

  // Expand each content row into per-target cards, then filter client-side
  // by platform + query. (Server already filtered status via refreshContent.)
  // Title + snippet are computed per-card so a multi-target post (e.g. one
  // content_item with separate Instagram/Threads/Telegram variants) shows
  // each platform's own copy instead of a single shared blurb.
  const filteredCards = useMemo(() => {
    const cards = (content || []).flatMap((row) => {
      const expanded = expandContentToCards(row);
      return expanded.map((card) => ({
        ...card,
        _title: deriveTitle(row, card.platform),
        _snippet: deriveSnippet(row, card.platform),
      }));
    });
    return cards.filter((card) => {
      if (platform !== 'all' && card.platform !== platform) return false;
      if (query) {
        const needle = query.toLowerCase();
        if (!card._title.toLowerCase().includes(needle) && !card._snippet.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [content, platform, query]);

  // Summary aggregates over per-target metrics across all content so each
  // platform publication is counted, not just the content_item's first target.
  const summary = useMemo(() => {
    const c = counts || {};
    const published = c.published || 0;
    let totalViews = 0;
    let totalEng = 0;
    let engCount = 0;
    for (const row of content || []) {
      const targets = row?.publishSummary?.targets || [];
      if (targets.length === 0) {
        const m = row?.metrics;
        if (!m) continue;
        totalViews += m.views || 0;
        const e = computeEngagement(m);
        if (typeof e === 'number') { totalEng += e; engCount++; }
        continue;
      }
      for (const t of targets) {
        const m = t.metrics;
        if (!m) continue;
        totalViews += m.views || 0;
        const e = computeEngagement(m);
        if (typeof e === 'number') { totalEng += e; engCount++; }
      }
    }
    const avgEng = engCount > 0 ? totalEng / engCount : null;
    return { published, totalViews, avgEng };
  }, [counts, content]);

  return (
    <div className="lib-root">
      <div className="lib-top">
        <div>
          <h1 className="lib-h">Library</h1>
          <p className="lib-sub">
            Everything you've published, scheduled, or drafted. Metrics live with their posts.
          </p>
        </div>
        <div className="lib-stats">
          <div className="lib-stat">
            <div className="lib-stat-l">Published</div>
            <div className="lib-stat-v">
              {summary.published}
              <span className="lib-stat-u">posts</span>
            </div>
          </div>
          <div className="lib-stat">
            <div className="lib-stat-l">Total views</div>
            <div className="lib-stat-v">
              {formatMetric(summary.totalViews)}
              <span className="lib-stat-u">impr.</span>
            </div>
          </div>
          <div className="lib-stat">
            <div className="lib-stat-l">Avg engagement</div>
            <div className="lib-stat-v">
              {summary.avgEng != null ? summary.avgEng.toFixed(1) : '—'}
              <span className="lib-stat-u">%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="lib-tabs">
        {tabsWithCounts.map((t) => (
          <button
            key={t.id}
            className={`lib-tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
            type="button"
          >
            {t.label}
            <span className="lib-tab-c mono">{t.count}</span>
          </button>
        ))}
      </div>

      <div className="lib-bar">
        <div className="lib-search">
          <I.search size={14} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search posts…"
          />
          {query && (
            <button
              className="lib-search-clear"
              onClick={() => setQuery('')}
              type="button"
              aria-label="Clear search"
            >
              <I.x size={12} />
            </button>
          )}
        </div>

        <div className="lib-plat-filter">
          {PLATFORMS.map((p) => (
            <button
              key={p}
              className={`lib-plat-chip ${platform === p ? 'is-active' : ''}`}
              onClick={() => setPlatform(p)}
              title={p}
              type="button"
            >
              {p === 'all' ? (
                <span style={{ fontSize: 11, padding: '0 4px' }}>All</span>
              ) : (
                <span
                  className={`ob-net-ic-${p}`}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  {BrandIcons[p]?.({ size: 11 })}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="lib-bar-r">
          <div className="cp-view-toggle">
            <button
              className={view === 'grid' ? 'is-active' : ''}
              onClick={() => setView('grid')}
              title="Grid"
              type="button"
            >
              <I.grid size={13} />
            </button>
            <button
              className={view === 'list' ? 'is-active' : ''}
              onClick={() => setView('list')}
              title="List"
              type="button"
            >
              <I.list size={13} />
            </button>
          </div>
        </div>
      </div>

      {loading && (!content || content.length === 0) ? (
        <div className="lib-empty">
          <I.refresh size={20} />
          <h3>Loading library…</h3>
        </div>
      ) : filteredCards.length === 0 ? (
        <div className="lib-empty">
          <I.library size={28} />
          <h3>No posts match</h3>
          <p>Try a different platform, status, or search term.</p>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setTab('all');
              setPlatform('all');
              setQuery('');
            }}
            type="button"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div className={`lib-grid ${view === 'list' ? 'is-list' : ''}`}>
            {filteredCards.map((card) => {
              const row = card.contentRow;
              const item = row.contentItem || row;
              const id = item.id;
              const status = card.status;
              // If the post is still mid-generation, the preview screen has no
              // copy/media to render — send the user to the batch progress
              // screen instead so they see live status. Once the batch finishes
              // they can reopen the preview from there.
              const batchId = item.generationBatchId || row.generationBatchId || null;
              // Anything that hasn't reached a viewable end-state goes to the
              // batch screen — preview is for finished posts only. Avoids
              // landing on an empty preview while the worker is still writing
              // the draft or generating visuals.
              const READY_STATUSES = new Set(['ready', 'scheduled', 'published', 'failed', 'archived']);
              const generationDone = !item.generationStatus || ['ready', 'failed'].includes(item.generationStatus);
              const isPreviewable = READY_STATUSES.has(status) && generationDone;
              // Preserve platform/target hint on the URL so PostPreview and
              // EditPost can scope their chrome (and the per-account picker)
              // to THIS specific row instead of falling back to the first
              // platform in the batch.
              const hintQs = (() => {
                const params = new URLSearchParams();
                if (card.targetId) params.set('target', card.targetId);
                if (card.platform) params.set('platform', card.platform);
                return params.toString();
              })();
              const hintSuffix = hintQs ? `?${hintQs}` : '';
              const onPreview = () => {
                if (!isPreviewable && batchId) {
                  navigate(`/app/batch/${encodeURIComponent(batchId)}`);
                  return;
                }
                navigate(`/app/post/${encodeURIComponent(id)}${hintSuffix}`);
              };
              const onEdit = () => navigate(`/app/post/${encodeURIComponent(id)}/edit${hintSuffix}`);
              const actionLabel =
                status === 'attention' || status === 'failed' ? 'Review' :
                status === 'draft' ? 'Edit' :
                status === 'published' ? 'Open' :
                status === 'scheduled' || status === 'planned' || status === 'ready' ? 'Reschedule' :
                null;
              const coverImage = deriveCoverImage(row);
              const cardTitle = card._title;
              const fullSnippet = card._snippet;
              const showSnippet =
                fullSnippet && !cardTitle.replace(/…$/, '').startsWith(fullSnippet.slice(0, 30))
                  && !fullSnippet.startsWith(cardTitle.replace(/…$/, ''));
              return (
                <LibraryCard
                  key={card.key}
                  id={id}
                  platform={card.platform}
                  status={status}
                  title={cardTitle}
                  snippet={showSnippet ? fullSnippet : ''}
                  date={deriveCardDate(card)}
                  media={deriveMedia(row)}
                  metrics={card.metrics ? {
                    views: card.metrics.views ?? 0,
                    likes: card.metrics.likes ?? 0,
                    comments: card.metrics.comments ?? 0,
                    shares: card.metrics.shares ?? 0,
                    engagement: computeEngagement(card.metrics),
                  } : null}
                  imageUrl={coverImage}
                  href={(() => {
                    const params = new URLSearchParams();
                    if (card.targetId) params.set('target', card.targetId);
                    if (card.platform) params.set('platform', card.platform);
                    const qs = params.toString();
                    return `/app/post/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`;
                  })()}
                  onPreview={onPreview}
                  onEdit={onEdit}
                  onAction={
                    actionLabel === 'Edit' ? onEdit :
                    actionLabel === 'Open' || actionLabel === 'Review' ? onPreview :
                    actionLabel === 'Reschedule' ? () => navigate('/app/calendar') :
                    null
                  }
                  actionLabel={actionLabel}
                />
              );
            })}
          </div>

          {/* Infinite-scroll sentinel + loading footer */}
          {hasMore && (
            <div
              ref={sentinelRef}
              style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-subtle)', fontSize: 12 }}
            >
              {loading ? 'Loading more…' : 'Scroll for more'}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Library;
